import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import {
  FolderKanban,
  Users,
  FileText,
  Clock,
  CalendarDays,
  ChevronRight,
  Banknote,
  AlertCircle,
  ClipboardList,
  ShieldCheck,
  CreditCard,
  Wifi,
  Send,
  Mail,
  RotateCw,
  Trash2
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { LiabilitiesByBankWidget } from '@/components/LiabilitiesByBankWidget';
import { WorkflowAlertsCard } from '@/components/WorkflowAlertsCard';
import { TodayFocusCard } from '@/components/dashboard/TodayFocusCard';
import { HoursWeekCard } from '@/components/dashboard/HoursWeekCard';
import { StatusDigestCard } from '@/components/dashboard/StatusDigestCard';
import { NeedsAttentionCard } from '@/components/dashboard/NeedsAttentionCard';
import { BizDevRow } from '@/components/dashboard/BizDevRow';
import { usePinnedSticky, PinnedStickyCard } from '@/components/dashboard/PinnedSticky';

/**
 * MeasuredChartContainer: lightweight replacement for Recharts' ResponsiveContainer
 * that avoids the "width(-1) height(-1)" warning by NOT mounting the chart until
 * the wrapper div has been measured with positive dimensions.
 *
 * Uses ResizeObserver (no debounce edge cases). Once a positive size is observed,
 * the chart renders with explicit pixel dimensions and re-renders on subsequent
 * size changes.
 */
function MeasuredChartContainer({ className, testId, children }) {
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setSize(prev => (prev.width !== w || prev.height !== h ? { width: w, height: h } : prev));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className={className} data-testid={testId}>
      {size.width > 0 && size.height > 0 ? children(size.width, size.height) : null}
    </div>
  );
}

// Simple stat card: label + big DM Mono number + optional subtitle.
// `valueColor` is a Tailwind class (e.g. 'text-primary', 'text-amber-400').
// Defaults to white (foreground). No decorative icons per spec.
const StatCard = ({ title, value, valueColor = 'text-foreground', to, navigate, subtitle, span }) => (
  <Card
    className={`border-white/10 bg-card shadow-none transition-colors ${to ? 'hover:bg-secondary/50 cursor-pointer' : ''} ${span === 2 ? 'sm:col-span-2' : ''}`}
    onClick={() => to && navigate(to)}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-4">
      <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{title}</p>
      <p className={`text-2xl sm:text-[28px] lg:text-[32px] font-medium font-mono tabular-nums leading-none mt-2 ${valueColor}`}>{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1.5">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Active Project Backlog card (Owner + Administrator only).
// Shows total contract value across active projects, remaining-to-bill,
// and a progress bar of billed-to-date percentage.
function ActiveProjectBacklogCard({ stats, navigate }) {
  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = stats.backlog_percent_billed || 0;
  const tcv = stats.backlog_total_contract_value || 0;
  const remaining = stats.backlog_remaining || 0;
  const billed = stats.backlog_billed_to_date || 0;

  return (
    <Card
      className="border-white/10 bg-card shadow-none hover:bg-secondary/10 transition-colors cursor-pointer"
      onClick={() => navigate('/invoices')}
      data-testid="active-project-backlog-card"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground truncate">Active Project Backlog</p>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0">{stats.backlog_active_project_count || 0} projects</span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate">Total Contract Value</span>
            <span className="text-base md:text-lg font-medium font-mono tabular-nums leading-tight shrink-0" data-testid="backlog-total-contract-value">{fmt(tcv)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate">Remaining to Bill</span>
            <span className="text-base md:text-lg font-medium font-mono tabular-nums text-primary leading-tight shrink-0" data-testid="backlog-remaining">{fmt(remaining)}</span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/[0.08]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Billed to date</span>
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="backlog-percent">{pct}%</span>
          </div>
          <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${Math.min(100, pct)}%` }}
              data-testid="backlog-progress-bar"
            />
          </div>
          <p className="text-xs text-muted-foreground/70 mt-1.5 tabular-nums">{fmt(billed)} billed</p>
        </div>
      </CardContent>
    </Card>
  );
}

// Task Completion card — shared by Employee + Owner dashboards. Personal scope.
// Clicking navigates to /tasks?mine=1 which pre-applies the My To-Dos filter.
function TaskCompletionCard({ stats, navigate }) {
  const totalTasks = (stats.my_tasks_open || 0) + (stats.my_tasks_completed || 0);
  const completion = totalTasks > 0
    ? Math.round((stats.my_tasks_completed / totalTasks) * 100)
    : 0;
  return (
    <Card
      className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/10 transition-colors"
      onClick={() => navigate('/tasks?mine=1')}
      data-testid="task-completion-card"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground truncate">Task Completion</p>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0">{stats.my_tasks_completed || 0}/{totalTasks}</span>
        </div>
        <div className="flex items-center gap-3">
          <Progress value={completion} className="flex-1 h-2" />
          <span className="text-base md:text-lg font-medium font-mono tabular-nums text-primary w-14 text-right shrink-0">{completion}%</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 tabular-nums">
          {stats.my_tasks_completed || 0} completed · {stats.my_tasks_open || 0} remaining
        </p>
      </CardContent>
    </Card>
  );
}

function PayrollSummaryCard({ payrollInfo, navigate }) {
  if (!payrollInfo) return null;
  return (
    <Card
      className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors"
      onClick={() => navigate('/accounting?tab=payroll')}
      data-testid="dashboard-payroll-card"
    >
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Banknote className="h-3.5 w-3.5 text-primary" /> Payroll Summary
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground ml-auto" />
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-normal">Pay Period: <span className="font-mono tabular-nums">{payrollInfo.pay_period_start} → {payrollInfo.pay_period_end}</span></p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {(() => {
                const endDate = new Date(payrollInfo.pay_period_end + 'T23:59:59');
                const now = new Date();
                const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                if (daysLeft > 0) return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`;
                if (daysLeft === 0) return 'Ends today';
                return `Ended ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`;
              })()}
            </p>
          </div>
          <Badge variant="outline" className={`text-xs ${
            payrollInfo.stage === 'Awaiting Time / Processing' ? 'bg-amber-500/15 text-amber-400' :
            payrollInfo.stage === 'Pending Review' ? 'bg-blue-500/15 text-blue-400' :
            payrollInfo.stage === 'Approved' ? 'bg-emerald-500/15 text-emerald-400' :
            'bg-violet-500/15 text-violet-400'
          }`}>
            {payrollInfo.stage}
          </Badge>
        </div>
        {payrollInfo.stage === 'Awaiting Time / Processing' && new Date() > new Date(payrollInfo.pay_period_end + 'T23:59:59') && (
          <div className="flex items-center gap-1.5 text-xs text-amber-400 mt-2">
            <AlertCircle className="h-3 w-3 shrink-0" /> Pay period has ended. Ready for review
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentActivityCards({ stats, navigate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/proposals')}>
        <CardHeader className="pb-2 border-b border-white/[0.08]">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-primary" /> Recent Proposals
            <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{(stats.recent_proposals || []).length}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 pt-2">
          {(stats.recent_proposals || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No proposals yet</p>
          ) : (
            stats.recent_proposals.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
                <div>
                  <p className="text-sm font-normal"><span className={p.proposal_number ? 'font-mono tabular-nums' : ''}>{p.proposal_number || p.name || 'Untitled'}</span></p>
                  <p className="text-xs text-muted-foreground">{p.proposal_number ? p.name : ''}{p.contact_name ? (p.proposal_number && p.name ? ` · ${p.contact_name}` : p.contact_name) : ''}</p>
                </div>
                <Badge variant="outline" className="text-xs">{p.stage}</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/projects')}>
        <CardHeader className="pb-2 border-b border-white/[0.08]">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FolderKanban className="h-3.5 w-3.5 text-primary" /> Recent Projects
            <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{(stats.recent_projects || []).length}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 pt-2">
          {(stats.recent_projects || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No projects yet</p>
          ) : (
            stats.recent_projects.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
                <div>
                  <p className="text-sm font-mono tabular-nums">{p.project_number}</p>
                  <p className="text-xs text-muted-foreground">{p.name}</p>
                </div>
                <Badge variant="outline" className="text-xs">{p.status}</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function APAlertsCard({ stats, navigate }) {
  const fmtMoney = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (!stats.ap_pending_count && !stats.ap_overdue) return null;

  return (
    <Card
      className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors"
      onClick={() => navigate('/accounting?tab=payable')}
      data-testid="dashboard-ap-alerts"
    >
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CreditCard className="h-3.5 w-3.5 text-primary" /> Accounts Payable
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground ml-auto" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Outstanding</p>
            <p className="text-[22px] font-medium font-mono tabular-nums text-amber-400 leading-tight mt-0.5">{fmtMoney(stats.ap_outstanding)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Active Bills</p>
            <p className="text-[22px] font-medium font-mono tabular-nums leading-tight mt-0.5">{stats.ap_pending_count || 0}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Overdue</p>
            <p className={`text-[22px] font-medium font-mono tabular-nums leading-tight mt-0.5 ${stats.ap_overdue > 0 ? 'text-red-400' : ''}`}>{stats.ap_overdue || 0}</p>
          </div>
        </div>
        {(stats.ap_due_soon || []).length > 0 && (
          <div>
            <p className="text-xs uppercase text-muted-foreground tracking-wider mb-1">Upcoming Due</p>
            {stats.ap_due_soon.map((ap) => (
              <div key={ap.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-normal truncate">{ap.vendor_name}</p>
                  <p className={`text-xs tabular-nums ${ap.due_date < new Date().toISOString().slice(0, 10) ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>{ap.due_date}</p>
                </div>
                <span className="text-sm font-medium font-mono tabular-nums text-amber-400 shrink-0 ml-2">{fmtMoney(ap.balance_due)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmailStatsWidget() {
  const RANGES = [
    { key: 'all', label: 'All time', days: null },
    { key: '7d', label: 'Last 7 days', days: 7 },
    { key: '30d', label: 'Last 30 days', days: 30 },
    { key: '90d', label: 'Last 90 days', days: 90 },
    { key: 'custom', label: 'Custom', days: null },
  ];
  const [rangeKey, setRangeKey] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, skipped: 0, retryable: 0, by_category: {}, by_day: [] });
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logStatus, setLogStatus] = useState('failed');
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [retryingSingle, setRetryingSingle] = useState(null);

  const computeDates = () => {
    if (rangeKey === 'all') return {};
    if (rangeKey === 'custom') return { date_from: customFrom || undefined, date_to: customTo || undefined };
    const r = RANGES.find(x => x.key === rangeKey);
    if (!r?.days) return {};
    const to = new Date();
    const from = new Date(Date.now() - r.days * 24 * 60 * 60 * 1000);
    return { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) };
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = computeDates();
      const qs = new URLSearchParams();
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      const r = await api.get(`/admin/email-stats${qs.toString() ? `?${qs.toString()}` : ''}`);
      setStats(r.data);
    } catch {
      // non-admin or fetch failed; component hides itself via role gate in parent
    } finally { setLoading(false); }
  };

  
  useEffect(() => {
    if (rangeKey !== 'custom' || (customFrom && customTo)) fetchStats();
  }, [rangeKey, customFrom, customTo]);
  

  const retryFailed = async () => {
    if (retrying || !stats.retryable) return;
    if (!window.confirm(`Retry ${stats.retryable} failed email${stats.retryable === 1 ? '' : 's'}?`)) return;
    setRetrying(true);
    try {
      const params = computeDates();
      const qs = new URLSearchParams();
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      const r = await api.post(`/admin/email-retry-failed${qs.toString() ? `?${qs.toString()}` : ''}`);
      toast.success(`Queued ${r.data.retried} email${r.data.retried === 1 ? '' : 's'} for retry`);
      // Re-fetch after a short delay to let the Resend attempts complete
      setTimeout(fetchStats, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    } finally { setRetrying(false); }
  };

  const fetchLog = async (status) => {
    setLogLoading(true);
    try {
      const params = computeDates();
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      const r = await api.get(`/admin/email-log?${qs.toString()}`);
      setLogEntries(r.data.entries || []);
    } catch { setLogEntries([]); } finally { setLogLoading(false); }
  };

  const openLog = (status) => {
    setLogStatus(status);
    setLogOpen(true);
    fetchLog(status);
  };

  // Re-fetch log whenever the status tab changes inside the modal; also collapse any expanded row
  
  useEffect(() => {
    if (logOpen) {
      setExpandedId(null);
      setExpandedDetail(null);
      fetchLog(logStatus);
    }
  }, [logStatus]);
  

  const toggleExpand = async (entry) => {
    if (expandedId === entry.id) {
      setExpandedId(null); setExpandedDetail(null); return;
    }
    setExpandedId(entry.id); setExpandedDetail(null); setExpandedLoading(true);
    try {
      const r = await api.get(`/admin/email-log/${entry.id}`);
      setExpandedDetail(r.data);
    } catch { setExpandedDetail(null); } finally { setExpandedLoading(false); }
  };

  const retrySingle = async (entry) => {
    if (retryingSingle) return;
    setRetryingSingle(entry.id);
    try {
      const r = await api.post(`/admin/email-log/${entry.id}/retry`);
      toast.success(`Retried: ${r.data.to}`);
      setTimeout(() => { fetchLog(logStatus); fetchStats(); }, 3000);
      setExpandedId(null); setExpandedDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    } finally { setRetryingSingle(null); }
  };

  const deleteSingle = async (entry) => {
    if (!window.confirm(`Permanently delete this ${entry.status} email log entry? Total counts stay intact (we keep an archive count for historical accuracy).`)) return;
    try {
      await api.delete(`/admin/email-log/${entry.id}`);
      toast.success('Deleted');
      setExpandedId(null); setExpandedDetail(null);
      await fetchLog(logStatus);
      await fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const bulkDeleteFailed = async () => {
    if (bulkDeleting) return;
    const { date_from, date_to } = computeDates();
    if (!window.confirm(`Permanently delete ALL failed email log entries${date_from || date_to ? ' in the selected date range' : ''}? This cannot be undone. Total counts stay intact.`)) return;
    setBulkDeleting(true);
    try {
      const body = { status: 'failed' };
      if (date_from) body.date_from = date_from;
      if (date_to) body.date_to = date_to;
      const r = await api.post('/admin/email-log/bulk-delete', body);
      toast.success(`Deleted ${r.data.deleted} failed email log entr${r.data.deleted === 1 ? 'y' : 'ies'}`);
      await fetchLog(logStatus);
      await fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  const deliveryRate = stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0;
  const topCategories = Object.entries(stats.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 4);

  // Fill in zero-count days across the selected range so the sparkline renders a continuous line.
  const filledByDay = (() => {
    const raw = stats.by_day || [];
    const map = Object.fromEntries(raw.map(d => [d.date, d.count]));
    const { date_from, date_to } = computeDates();
    let start, end;
    if (date_from && date_to) {
      start = new Date(date_from + 'T00:00:00'); end = new Date(date_to + 'T00:00:00');
    } else if (raw.length) {
      start = new Date(raw[0].date + 'T00:00:00');
      end = new Date(raw[raw.length - 1].date + 'T00:00:00');
    } else {
      return [];
    }
    // cap at 180 points to keep render light
    const out = []; const cursor = new Date(start);
    let safety = 0;
    while (cursor <= end && safety < 200) {
      const iso = cursor.toISOString().slice(0, 10);
      out.push({ date: iso, count: map[iso] || 0 });
      cursor.setDate(cursor.getDate() + 1);
      safety++;
    }
    return out;
  })();
  const showChart = filledByDay.length >= 2;
  const shortDate = (iso) => {
    try { const d = new Date(iso + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return iso; }
  };

  return (
    <Card className="border-border bg-card shadow-none" data-testid="admin-email-stats-widget">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-heading">Email Deliverability</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRangeKey(r.key)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${rangeKey === r.key ? 'bg-primary text-primary-foreground' : 'bg-secondary/40 text-muted-foreground hover:bg-secondary/60'}`}
                data-testid={`email-stats-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {rangeKey === 'custom' && (
          <div className="flex items-center gap-2 pt-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-8 text-xs bg-background border border-border rounded px-2" data-testid="email-stats-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-8 text-xs bg-background border border-border rounded px-2" data-testid="email-stats-to" />
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => openLog('')}
            className="text-left rounded-md border border-border bg-secondary/15 px-3 py-2.5 hover:bg-secondary/25 transition-colors"
            data-testid="email-stats-total"
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p>
            <p className="text-2xl font-heading font-bold text-foreground">{loading ? '-' : stats.total.toLocaleString()}</p>
          </button>
          <button
            type="button"
            onClick={() => openLog('sent')}
            className="text-left rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 hover:bg-emerald-500/10 transition-colors"
            data-testid="email-stats-sent"
          >
            <p className="text-xs uppercase tracking-wider text-emerald-400">Delivered</p>
            <p className="text-2xl font-heading font-bold text-emerald-400">{loading ? '-' : stats.sent.toLocaleString()}</p>
          </button>
          <div className="rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2.5" data-testid="email-stats-failed">
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={() => openLog('failed')}
                className="text-left min-w-0 flex-1"
                data-testid="email-stats-failed-open-log"
              >
                <p className="text-xs uppercase tracking-wider text-red-400">Failed</p>
                <p className="text-2xl font-heading font-bold text-red-400">{loading ? '-' : stats.failed.toLocaleString()}</p>
              </button>
              {stats.retryable > 0 && (
                <button
                  onClick={retryFailed}
                  disabled={retrying}
                  className="text-xs shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                  title={`Retry ${stats.retryable} failed email${stats.retryable === 1 ? '' : 's'}`}
                  data-testid="email-retry-failed-btn"
                >
                  <RotateCw className={`h-3 w-3 ${retrying ? 'animate-spin' : ''}`} />
                  Retry {stats.retryable}
                </button>
              )}
            </div>
          </div>
          <div className="rounded-md border border-border bg-secondary/15 px-3 py-2.5" data-testid="email-stats-rate">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Delivery Rate</p>
            <p className={`text-2xl font-heading font-bold ${deliveryRate >= 95 ? 'text-emerald-400' : deliveryRate >= 80 ? 'text-amber-400' : 'text-red-400'}`}>{loading ? '-' : `${deliveryRate}%`}</p>
          </div>
        </div>

        {/* Sparkline - daily delivered volume across the selected range.
            Wrapper measures its own width via ResizeObserver and only mounts the
            chart once width > 0 to avoid the Recharts "-1 width" warning. */}
        {showChart && (
          <MeasuredChartContainer className="mt-4 h-28 -mx-1 w-full" testId="email-stats-sparkline">
            {(width, height) => (
              <LineChart width={width} height={height} data={filledByDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <RechartsTooltip
                  cursor={{ stroke: 'hsl(var(--primary))', strokeDasharray: '3 3' }}
                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: 11 }}
                  labelFormatter={shortDate}
                  formatter={(value) => [value, 'Emails']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3, fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            )}
          </MeasuredChartContainer>
        )}

        {topCategories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="email-stats-categories">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold self-center mr-1">By type:</span>
            {topCategories.map(([cat, cnt]) => (
              <span key={cat} className="text-xs px-2 py-0.5 rounded-full bg-secondary/40 text-muted-foreground">
                {cat} <span className="font-semibold text-foreground ml-0.5">{cnt}</span>
              </span>
            ))}
          </div>
        )}
        {stats.skipped > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            <span className="text-zinc-400">Skipped:</span> {stats.skipped} (recipient opted-out or Resend unconfigured)
          </p>
        )}

        {/* ─── Resend Daily Budget ─── */}
        {stats.budget && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="email-budget-block">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Resend Daily Budget</p>
              {stats.budget.days_exceeded_30d > 0 && (
                <span className="text-xs text-amber-400" data-testid="email-budget-throttle-counter">
                  Throttle kicked in {stats.budget.days_exceeded_30d} day{stats.budget.days_exceeded_30d === 1 ? '' : 's'} in last 30
                </span>
              )}
            </div>
            {(() => {
              const used = stats.budget.today_sent || 0;
              const limit = stats.budget.daily_limit || 100;
              const pct = Math.min(100, Math.round((used / limit) * 100));
              const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
              return (
                <>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <p className="text-sm">
                      <span className="font-heading font-bold text-lg text-foreground" data-testid="email-budget-used">{used}</span>
                      <span className="text-muted-foreground text-xs"> / {limit} today</span>
                    </p>
                    <span className="text-xs text-muted-foreground" data-testid="email-budget-remaining">
                      {stats.budget.today_remaining} remaining
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary/40 overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} data-testid="email-budget-bar" />
                  </div>
                </>
              );
            })()}
            {(stats.budget.deferred_queue > 0 || stats.deferred > 0) && (
              <button
                type="button"
                onClick={() => openLog('deferred')}
                className="mt-2 w-full text-left rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 hover:bg-amber-500/15 transition-colors"
                data-testid="email-budget-deferred-open-log"
              >
                <p className="text-xs text-amber-400 font-medium">
                  {stats.budget.deferred_queue} email{stats.budget.deferred_queue === 1 ? '' : 's'} queued for later
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Legacy queue — migrated into the outbox and sent by the dispatcher
                </p>
              </button>
            )}
            {stats.budget.last_exceeded && (
              <p className="text-[11px] text-muted-foreground mt-2" data-testid="email-budget-last-exceeded">
                Last exceeded: {stats.budget.last_exceeded.date}
                {stats.budget.last_exceeded.deferred_count ? ` (${stats.budget.last_exceeded.deferred_count} deferred)` : ''}
              </p>
            )}
          </div>
        )}

        {/* ─── Email Outbox (deferral queue) status ─── */}
        {stats.outbox && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="email-outbox-block">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Outbox Queue</p>
            <div className="grid grid-cols-4 gap-2">
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-2 text-center" data-testid="email-outbox-queued">
                <p className="font-heading font-bold text-lg text-amber-400">{stats.outbox.queued ?? 0}</p>
                <p className="text-[11px] text-muted-foreground">Queued</p>
              </div>
              <div className="rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-2 text-center" data-testid="email-outbox-sending">
                <p className="font-heading font-bold text-lg text-blue-400">{stats.outbox.sending ?? 0}</p>
                <p className="text-[11px] text-muted-foreground">Sending</p>
              </div>
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-2 text-center" data-testid="email-outbox-sent">
                <p className="font-heading font-bold text-lg text-emerald-400">{stats.outbox.sent ?? 0}</p>
                <p className="text-[11px] text-muted-foreground">Sent</p>
              </div>
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-2 text-center" data-testid="email-outbox-failed">
                <p className="font-heading font-bold text-lg text-red-400">{stats.outbox.failed ?? 0}</p>
                <p className="text-[11px] text-muted-foreground">Failed</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2" data-testid="email-outbox-next">
              {stats.outbox.next_scheduled_for
                ? `Next scheduled send: ${new Date(stats.outbox.next_scheduled_for).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })} ET`
                : 'No emails waiting in the queue'}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Overnight emails (12:00–6:59 AM ET) are held and sent at 7:00 AM ET.
            </p>
          </div>
        )}
        {stats.delivery_status && Object.keys(stats.delivery_status).length > 0 && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="email-delivery-status-block">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Delivery Status</p>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const colors = {
                  delivered: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
                  opened: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
                  clicked: 'text-sky-400 border-sky-500/30 bg-sky-500/10',
                  bounced: 'text-red-400 border-red-500/30 bg-red-500/10',
                  complained: 'text-red-500 border-red-600/30 bg-red-600/10',
                  delayed: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
                  sent_to_provider: 'text-muted-foreground border-border bg-secondary/40',
                };
                const labels = {
                  delivered: 'Delivered', opened: 'Opened', clicked: 'Clicked',
                  bounced: 'Bounced', complained: 'Marked spam', delayed: 'Delayed',
                  sent_to_provider: 'In transit',
                };
                return Object.entries(stats.delivery_status).map(([k, v]) => (
                  <span key={k} className={`text-xs px-2 py-0.5 rounded-full border ${colors[k] || 'text-muted-foreground border-border bg-secondary/40'}`}>
                    {labels[k] || k} <span className="font-semibold ml-0.5">{v}</span>
                  </span>
                ));
              })()}
            </div>
          </div>
        )}
      </CardContent>

      {/* Email Log Diagnostic Modal */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="email-log-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email Log</DialogTitle>
            <DialogDescription>Recent email attempts. Click a status tab to filter. Honors the current date range from the widget.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 border-b border-border pb-2">
            {[
              { key: '', label: 'All' },
              { key: 'sent', label: 'Delivered' },
              { key: 'failed', label: 'Failed' },
              { key: 'skipped', label: 'Skipped' },
              { key: 'deferred', label: 'Deferred' },
            ].map(t => (
              <button
                key={t.key || 'all'}
                onClick={() => setLogStatus(t.key)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${logStatus === t.key ? 'bg-primary text-primary-foreground' : 'bg-secondary/40 text-muted-foreground hover:bg-secondary/60'}`}
                data-testid={`email-log-tab-${t.key || 'all'}`}
              >
                {t.label}
              </button>
            ))}
            {logStatus === 'failed' && logEntries.length > 0 && (
              <button
                onClick={bulkDeleteFailed}
                disabled={bulkDeleting}
                className="ml-auto text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-zinc-500/30 bg-zinc-500/10 text-zinc-300 hover:bg-zinc-500/20 disabled:opacity-50"
                title="Permanently delete all failed entries in the current date range. Total counts stay accurate."
                data-testid="email-log-bulk-delete-failed"
              >
                <Trash2 className="h-3 w-3" /> {bulkDeleting ? 'Deleting…' : 'Delete all failed'}
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {logLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
            ) : logEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No entries for this filter.</p>
            ) : (
              <div className="divide-y divide-border">
                {logEntries.map(e => {
                  const d = new Date(e.sent_at);
                  const when = d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                  const statusColor = e.status === 'sent' ? 'text-emerald-400 bg-emerald-500/10'
                    : e.status === 'failed' ? 'text-red-400 bg-red-500/10'
                    : 'text-zinc-400 bg-zinc-500/10';
                  const isExpanded = expandedId === e.id;
                  const canRetry = e.status === 'failed' && !e.retried;
                  return (
                    <div key={e.id} data-testid={`email-log-entry-${e.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleExpand(e)}
                        className="w-full text-left py-2.5 text-xs hover:bg-secondary/20 transition-colors px-2 -mx-2 rounded"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-1.5 py-0.5 rounded uppercase font-semibold ${statusColor}`}>{e.status}{e.retried ? ' · retried' : ''}</span>
                          <span className="text-foreground font-medium truncate flex-1">{e.subject}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{isExpanded ? '▾' : '▸'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-muted-foreground mt-1 text-xs flex-wrap">
                          <span>→ {e.to}</span>
                          <span>·</span>
                          <span>{e.category}</span>
                          <span>·</span>
                          <span>{when}</span>
                        </div>
                        {e.error && !isExpanded && (
                          <p className="text-xs text-red-400/80 mt-1 font-mono truncate" title={e.error}>{e.error}</p>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="mx-2 mt-1 mb-2 rounded-md border border-border bg-secondary/20 p-3 space-y-2" data-testid={`email-log-expanded-${e.id}`}>
                          {expandedLoading && <p className="text-xs text-muted-foreground">Loading preview…</p>}
                          {expandedDetail && (
                            <>
                              {expandedDetail.error && (
                                <div>
                                  <p className="text-xs uppercase tracking-wider font-semibold text-red-400">Send Error</p>
                                  <p className="text-xs text-red-300 font-mono break-words">{expandedDetail.error}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">HTML Preview</p>
                                {expandedDetail.html ? (
                                  <iframe
                                    title="email preview"
                                    srcDoc={expandedDetail.html}
                                    className="w-full h-64 rounded border border-border bg-white"
                                    sandbox=""
                                  />
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">HTML body not retained (only stored for failed/skipped attempts).</p>
                                )}
                              </div>
                              {canRetry && expandedDetail.html && (
                                <div className="flex justify-end pt-1 gap-1.5">
                                  <button
                                    onClick={(ev) => { ev.stopPropagation(); retrySingle(e); }}
                                    disabled={retryingSingle === e.id}
                                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                                    data-testid={`email-log-retry-single-${e.id}`}
                                  >
                                    <RotateCw className={`h-3 w-3 ${retryingSingle === e.id ? 'animate-spin' : ''}`} />
                                    Retry this email
                                  </button>
                                  <button
                                    onClick={(ev) => { ev.stopPropagation(); deleteSingle(e); }}
                                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-zinc-500/30 bg-zinc-500/10 text-zinc-400 hover:bg-zinc-500/20 transition-colors"
                                    data-testid={`email-log-delete-single-${e.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    Delete
                                  </button>
                                </div>
                              )}
                              {!canRetry && (
                                <div className="flex justify-end pt-1">
                                  <button
                                    onClick={(ev) => { ev.stopPropagation(); deleteSingle(e); }}
                                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-zinc-500/30 bg-zinc-500/10 text-zinc-400 hover:bg-zinc-500/20 transition-colors"
                                    data-testid={`email-log-delete-single-${e.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    Delete entry
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Showing latest {logEntries.length} entries{logEntries.length >= 100 ? ' (capped at 100)' : ''}.
          </p>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AdminDashboard({ stats, navigate, systemStats, loginActivity, systemErrorSummary }) {
  const [activeTab, setActiveTab] = useState('sessions');

  const activeSessions = systemStats.active_sessions || [];
  const activeErrors = systemErrorSummary?.active || 0;
  const highErrors = systemErrorSummary?.high || 0;

  return (
    <>
      {/* System Health Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-start" data-testid="admin-system-health">
        <StatCard title="Total Users" value={systemStats.total_users || 0} valueColor="text-foreground" to="/employees" navigate={navigate} />
        <StatCard title="Active Users" value={systemStats.active_users || 0} valueColor="text-foreground" to="/employees" navigate={navigate} />
        <StatCard title="Logins Today" value={systemStats.logins_today || 0} valueColor="text-foreground" navigate={navigate} />
        <StatCard title="Logins This Week" value={systemStats.logins_week || 0} valueColor="text-foreground" navigate={navigate} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-start" data-testid="admin-data-stats">
        <StatCard
          title="System Logs"
          value={activeErrors}
          valueColor={highErrors > 0 ? "text-red-400" : activeErrors > 0 ? "text-amber-400" : "text-[#4CAF50]"}
          to="/system-logs"
          navigate={navigate}
          subtitle={highErrors > 0 ? `${highErrors} high priority` : activeErrors > 0 ? "Active errors" : "All clear"}
        />
        <StatCard title="Total Invoices" value={systemStats.total_invoices || 0} valueColor="text-foreground" to="/invoices" navigate={navigate} />
        <StatCard title="Documents" value={systemStats.total_documents || 0} valueColor="text-foreground" to="/documents" navigate={navigate} />
        <StatCard title="Database Records" value={(systemStats.total_db_records || 0).toLocaleString()} valueColor="text-foreground" navigate={navigate} />
      </div>

      {/* Email Deliverability Widget - Resend backed */}
      <EmailStatsWidget />

      {/* Active Sessions + Login Activity - tabbed card */}
      <Card className="border-border bg-card shadow-none" data-testid="admin-sessions-activity">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab('sessions')}
              className={`text-sm font-heading font-bold flex items-center gap-1.5 pb-1 border-b-2 transition-colors ${activeTab === 'sessions' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="admin-tab-sessions"
            >
              <Wifi className="h-3.5 w-3.5" /> Active Sessions
              {activeSessions.length > 0 && (
                <span className="ml-1 h-5 min-w-[20px] px-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center justify-center">{activeSessions.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('logins')}
              className={`text-sm font-heading font-bold flex items-center gap-1.5 pb-1 border-b-2 transition-colors ${activeTab === 'logins' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="admin-tab-logins"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Login History
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'sessions' ? (
            activeSessions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No active sessions in the last 15 minutes</p>
            ) : (
              <div className="space-y-1.5" data-testid="admin-active-sessions-list">
                {activeSessions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-secondary/20 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{s.role}</Badge>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {(() => {
                          const mins = Math.round((Date.now() - new Date(s.last_active).getTime()) / 60000);
                          return mins < 1 ? 'just now' : `${mins}m ago`;
                        })()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            loginActivity.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No login activity recorded</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="admin-login-history-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-3 text-xs uppercase text-muted-foreground tracking-wider font-semibold">User</th>
                      <th className="text-left py-2 pr-3 text-xs uppercase text-muted-foreground tracking-wider font-semibold">Role</th>
                      <th className="text-left py-2 pr-3 text-xs uppercase text-muted-foreground tracking-wider font-semibold">Time</th>
                      <th className="text-left py-2 text-xs uppercase text-muted-foreground tracking-wider font-semibold">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginActivity.map((log) => (
                      <tr key={log.id} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-3">
                          <p className="font-medium">{log.user_name}</p>
                          <p className="text-muted-foreground">{log.email}</p>
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline" className="text-xs">{log.role}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-2 text-muted-foreground font-mono">{log.ip || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* AP Alerts for Administrator */}
      <APAlertsCard stats={stats} navigate={navigate} />

      {/* Workflow Alerts — stale contracts/invoices/projects */}
      <WorkflowAlertsCard navigate={navigate} />

      <HoursWeekCard navigate={navigate} />
    </>
  );
}

function OwnerDashboard({ stats, navigate, upcomingEvents }) {
  const fmtCurrency = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const priorityColors = {
    'High': 'bg-red-500/15 text-red-400',
    'Medium': 'bg-amber-500/15 text-amber-400',
    'Low': 'bg-zinc-500/15 text-zinc-400',
  };

  return (
    <>
      {/* Financial Overview - Total Revenue gets wider hierarchy span */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-start" data-testid="owner-financial-stats">
        <StatCard span={2} title="Total Revenue" value={fmtCurrency(stats.total_revenue)} valueColor="text-foreground" to="/accounting" navigate={navigate} />
        <StatCard title="Revenue This Month" value={fmtCurrency(stats.revenue_this_month)} valueColor="text-primary" to="/invoices" navigate={navigate} />
        <StatCard title="Outstanding Balance" value={fmtCurrency(stats.outstanding_balance)} valueColor="text-amber-400" to="/invoices" navigate={navigate} />
        <StatCard title="Overdue Invoices" value={stats.overdue_invoices || 0} valueColor={stats.overdue_invoices > 0 ? "text-red-400" : "text-foreground"} to="/invoices" navigate={navigate} />
      </div>

      {/* Business Overview — sketch layout:
          - Backlog spans 2 rows on the left (tall sidebar, 1/4 width)
          - Top right: 3 small cards in 3 cols (Approvals, Active Projects, Total Clients)
          - Bottom right: Task Completion spans those same 3 cols
          On mobile: stacks vertically. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 lg:grid-rows-2 gap-4 items-stretch" data-testid="owner-business-stats">
        <div className="lg:row-span-2">
          <ActiveProjectBacklogCard stats={stats} navigate={navigate} />
        </div>
        <StatCard
          title="Invoices Awaiting Your Approval"
          value={stats.invoices_pending_approval || 0}
          valueColor={stats.invoices_pending_approval > 0 ? "text-amber-400" : "text-foreground"}
          to="/invoices?status=Review"
          navigate={navigate}
          subtitle={stats.invoices_pending_approval > 0 ? "Pending review" : "All clear"}
        />
        <StatCard title="Active Projects" value={stats.active_projects || 0} valueColor="text-foreground" to="/projects" navigate={navigate} />
        <StatCard title="Total Clients" value={stats.total_clients || 0} valueColor="text-foreground" to="/clients" navigate={navigate} />
        <div className="lg:col-span-3">
          <TaskCompletionCard stats={stats} navigate={navigate} />
        </div>
      </div>

      {/* Liabilities + My Upcoming Tasks side-by-side, half-and-half */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="owner-liab-tasks-row">
        <LiabilitiesByBankWidget variant="dashboard" />
        <UpcomingTasksWidget tasks={stats.my_upcoming_tasks || []} priorityColors={priorityColors} navigate={navigate} />
      </div>

      {/* Accounts Payable + Upcoming Events side-by-side, half-and-half */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="owner-ap-events-row">
        <APAlertsCard stats={stats} navigate={navigate} />
        <UpcomingEventsWidget events={upcomingEvents} navigate={navigate} />
      </div>

      {/* Workflow Alerts — stale contracts/invoices/projects */}
      <WorkflowAlertsCard navigate={navigate} />

      <HoursWeekCard navigate={navigate} />

      <RecentActivityCards stats={stats} navigate={navigate} />
    </>
  );
}

// Extracted shareable widgets used in Owner + Employee dashboards.
function UpcomingTasksWidget({ tasks, priorityColors, navigate }) {
  return (
    <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/tasks')} data-testid="dashboard-upcoming-tasks">
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5 text-primary shrink-0" /> My Upcoming Tasks
          <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{tasks.length}</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0 pt-2">
        {tasks.length === 0 ? (
          <p className="text-xs text-muted-foreground">No open tasks</p>
        ) : (
          tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-normal truncate">{t.title}</p>
                {t.due_date && <p className="text-xs text-muted-foreground tabular-nums">Due: {t.due_date}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {t.priority && <Badge variant="outline" className={`text-xs ${(priorityColors || {})[t.priority] || ''}`}>{t.priority}</Badge>}
                <Badge variant="outline" className="text-xs">{t.status}</Badge>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function UpcomingEventsWidget({ events, navigate }) {
  return (
    <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/calendar')} data-testid="dashboard-calendar-card">
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5 text-primary shrink-0" /> Upcoming Events
          <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{events.length}</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0 pt-2">
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No upcoming events in the next 30 days</p>
        ) : (
          events.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-normal truncate">{ev.name}</p>
                <p className="text-xs text-muted-foreground truncate"><span className="font-mono tabular-nums">{ev.date}</span>{ev.project_name ? ` · ${ev.project_name}` : ''}</p>
              </div>
              {ev.event_type && <Badge variant="outline" className="text-xs shrink-0">{ev.event_type}</Badge>}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function OfficeManagerDashboard({ stats, navigate, payrollInfo }) {
  return (
    <>
      {/* Approval Queue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start" data-testid="om-approval-stats">
        <StatCard title="Timesheets Pending" value={stats.pending_timesheets || 0} valueColor={stats.pending_timesheets > 0 ? "text-amber-400" : "text-foreground"} to="/timesheets" navigate={navigate} subtitle={stats.pending_timesheets > 0 ? "Needs review" : "All clear"} />
        <StatCard title="Reimbursements Pending" value={stats.pending_reimbursements || 0} valueColor={stats.pending_reimbursements > 0 ? "text-amber-400" : "text-foreground"} to="/reimbursements" navigate={navigate} subtitle={stats.pending_reimbursements > 0 ? "Needs review" : "All clear"} />
        <StatCard
          title="Approved Invoices"
          value={stats.invoices_approved || 0}
          valueColor={stats.invoices_approved > 0 ? "text-primary" : "text-foreground"}
          to="/invoices?status=Approved"
          navigate={navigate}
          subtitle={stats.invoices_approved > 0 ? "Ready to send" : "None ready"}
        />
      </div>

      {/* Operations */}
      <div className="grid grid-cols-3 gap-4 items-start" data-testid="om-ops-stats">
        <StatCard title="Active Projects" value={stats.active_projects || 0} valueColor="text-foreground" to="/projects" navigate={navigate} />
        <StatCard title="Open Tasks" value={stats.open_tasks || 0} valueColor="text-foreground" to="/tasks" navigate={navigate} />
        <StatCard title="Overdue Invoices" value={stats.overdue_invoices || 0} valueColor={stats.overdue_invoices > 0 ? "text-red-400" : "text-foreground"} to="/invoices" navigate={navigate} />
      </div>

      <LiabilitiesByBankWidget variant="dashboard" />

      <APAlertsCard stats={stats} navigate={navigate} />
      <PayrollSummaryCard payrollInfo={payrollInfo} navigate={navigate} />
      <HoursWeekCard navigate={navigate} />
    </>
  );
}

function EmployeeDashboard({ stats, navigate, onTasksChanged }) {
  const { sticky, stickies, setPin, saveContent } = usePinnedSticky();

  return (
    <>
      {/* My Work Overview — pinned sticky note (when present) takes the first slot */}
      <div className={`grid grid-cols-2 gap-4 ${sticky ? 'md:grid-cols-3 xl:grid-cols-5' : 'md:grid-cols-4'}`} data-testid="employee-work-stats">
        {sticky && <PinnedStickyCard sticky={sticky} stickies={stickies} onSelect={setPin} onSave={saveContent} />}
        <StatCard title="My Projects" value={stats.my_projects || 0} valueColor="text-foreground" to="/projects" navigate={navigate} />
        <StatCard title="My Open Tasks" value={stats.my_tasks_open || 0} valueColor="text-foreground" to="/tasks" navigate={navigate} />
        <StatCard title="Hours This Period" value={stats.my_hours_this_period || 0} valueColor="text-primary" to="/timesheets" navigate={navigate} />
        <StatCard title="My Reimbursements" value={stats.my_pending_reimbursements || 0} valueColor={stats.my_pending_reimbursements > 0 ? "text-amber-400" : "text-foreground"} to="/reimbursements" navigate={navigate} subtitle={stats.my_pending_reimbursements > 0 ? "Pending" : "None pending"} />
      </div>

      {/* Today's Focus — prioritized to-dos + action hour + action items */}
      <TodayFocusCard navigate={navigate} onTasksChanged={onTasksChanged} />

      {/* Task Progress */}
      <TaskCompletionCard stats={stats} navigate={navigate} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Hours This Week by project */}
        <HoursWeekCard navigate={navigate} />

        {/* Recent Timesheets */}
        <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/timesheets')} data-testid="employee-recent-timesheets">
          <CardHeader className="pb-2 border-b border-white/[0.08]">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-primary" /> Recent Timesheets
              <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{(stats.my_recent_timesheets || []).length}</span>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0 pt-2">
            {(stats.my_recent_timesheets || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No timesheets yet</p>
            ) : (
              stats.my_recent_timesheets.map((ts) => (
                <div key={ts.week_start || ts.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
                  <div>
                    <p className="text-sm font-normal">Week of <span className="font-mono tabular-nums">{ts.week_start}</span></p>
                    <p className="text-xs text-muted-foreground tabular-nums">{ts.total_hours || 0} hours</p>
                  </div>
                  <Badge variant="outline" className={`text-xs ${
                    ts.status === 'Approved' ? 'bg-emerald-500/15 text-emerald-400' :
                    ts.status === 'Submitted' ? 'bg-blue-500/15 text-blue-400' :
                    ts.status === 'Rejected' ? 'bg-red-500/15 text-red-400' :
                    'bg-zinc-500/15 text-zinc-400'
                  }`}>{ts.status}</Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <StatusDigestCard navigate={navigate} />
        <NeedsAttentionCard navigate={navigate} />
      </div>

      <BizDevRow navigate={navigate} />
    </>
  );
}

export default function DashboardPage() {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [payrollInfo, setPayrollInfo] = useState(null);
  const [systemStats, setSystemStats] = useState({});
  const [loginActivity, setLoginActivity] = useState([]);
  const [systemErrorSummary, setSystemErrorSummary] = useState({});

  const refreshStats = () => api.get('/dashboard').then(r => setStats(r.data)).catch(() => {});

  useEffect(() => {
    api.get('/dashboard').then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));

    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 30);
    const end = futureDate.toISOString().slice(0, 10);
    api.get(`/calendar/events?start=${start}&end=${end}`).then(r => {
      setUpcomingEvents(r.data.slice(0, 5));
    }).catch(() => {});

    if (['Administrator', 'Owner', 'Office Manager'].includes(user?.role)) {
      api.get('/payroll-periods').then(r => {
        const periods = r.data || [];
        const active = periods.find(p => p.stage !== 'Paid / Distributed') || periods[0];
        if (active) setPayrollInfo(active);
      }).catch(() => {});
    }

    // Administrator-only: fetch system health stats + login activity + system errors
    if (user?.role === 'Administrator') {
      api.get('/auth/admin/system-stats').then(r => setSystemStats(r.data)).catch(() => {});
      api.get('/auth/admin/login-activity').then(r => setLoginActivity((r.data || []).slice(0, 20))).catch(() => {});
      api.get('/system-errors/summary').then(r => setSystemErrorSummary(r.data)).catch(() => {});
    }
  }, [user?.role]);
  if (loading) return <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>;

  const greetings = {
    'Administrator': 'System Health',
    'Owner': 'Business Overview',
    'Office Manager': 'Operations Overview',
  };
  const greeting = greetings[effectiveRole] || 'My Workspace';

  const renderDashboard = () => {
    const s = stats || {};
    switch (effectiveRole) {
      case 'Administrator':
        return <AdminDashboard stats={s} navigate={navigate} systemStats={systemStats} loginActivity={loginActivity} systemErrorSummary={systemErrorSummary} />;
      case 'Owner':
        return <OwnerDashboard stats={s} navigate={navigate} upcomingEvents={upcomingEvents} />;
      case 'Office Manager':
        return <OfficeManagerDashboard stats={s} navigate={navigate} payrollInfo={payrollInfo} />;
      default:
        return <EmployeeDashboard stats={s} navigate={navigate} onTasksChanged={refreshStats} />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Page header per design spec section 3: title + record/context label on the right, 1px divider below */}
      <div className="flex items-end justify-between pb-4 border-b border-white/10">
        <h1 className="text-[22px] font-medium tracking-tight">Dashboard</h1>
        <span className="text-sm text-muted-foreground/70" data-testid="dashboard-context">{greeting}</span>
      </div>

      {renderDashboard()}

      {/* Upcoming Calendar Events — rendered globally for non-Owner roles.
          Owner has this embedded inline next to Accounts Payable above. */}
      {effectiveRole !== 'Owner' && (
        <Card className="border-white/10 bg-card shadow-none cursor-pointer hover:bg-secondary/20 transition-colors" onClick={() => navigate('/calendar')} data-testid="dashboard-calendar-card-global">
        <CardHeader className="pb-2 border-b border-white/[0.08]">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-primary" /> Upcoming Events
            <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{upcomingEvents.length}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 pt-2">
          {upcomingEvents.length === 0 ? (
            <p className="text-xs text-muted-foreground">No upcoming events in the next 30 days</p>
          ) : (
            upcomingEvents.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-normal truncate">{ev.name}</p>
                  <p className="text-xs text-muted-foreground"><span className="font-mono tabular-nums">{ev.date}</span>{ev.project_name ? ` · ${ev.project_name}` : ''}</p>
                </div>
                {ev.event_type && <Badge variant="outline" className="text-xs shrink-0 ml-2">{ev.event_type}</Badge>}
              </div>
            ))
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
